'use client';

import { mutateSessionStorage, withSessionStorage } from '../../../utils/sessionStorageAccess';
import { isAuth0CallbackPath } from '../pendingAuth0ReturnTo';
import {
    ACTIVATE_OFFER_POST_LOGIN_ACTION,
    type OfferPostLoginParams,
    OPEN_OFFER_DETAILS_POST_LOGIN_ACTION,
    type PostLoginIntent,
} from './types';

export const POST_LOGIN_INTENT_SESSION_KEY = 'auth0:postLoginIntent';

function isOfferPostLoginParams(value: unknown): value is OfferPostLoginParams {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }

    const params = value as Record<string, unknown>;
    return (
        typeof params.offerId === 'string' && params.offerId.trim().length > 0 && typeof params.gtmSection === 'string'
    );
}

function isPostLoginIntent(value: unknown): value is PostLoginIntent {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return false;
    }

    const intent = value as Record<string, unknown>;
    switch (intent.action) {
        case OPEN_OFFER_DETAILS_POST_LOGIN_ACTION:
        case ACTIVATE_OFFER_POST_LOGIN_ACTION:
            return isOfferPostLoginParams(intent.params);
        default:
            return false;
    }
}

export function persistPostLoginIntent(intent?: PostLoginIntent) {
    if (!isPostLoginIntent(intent)) {
        mutateSessionStorage(storage => storage.removeItem(POST_LOGIN_INTENT_SESSION_KEY));
        return;
    }

    mutateSessionStorage(storage => storage.setItem(POST_LOGIN_INTENT_SESSION_KEY, JSON.stringify(intent)));
}

export function discardPostLoginIntent() {
    if (typeof window === 'undefined' || isAuth0CallbackPath(window.location.pathname)) {
        return;
    }

    mutateSessionStorage(storage => storage.removeItem(POST_LOGIN_INTENT_SESSION_KEY));
}

export async function doPostLoginAction<TAction extends PostLoginIntent['action']>(
    action: TAction,
    callback: (params: Extract<PostLoginIntent, { action: TAction }>['params']) => void | Promise<void>
): Promise<void> {
    if (typeof window === 'undefined' || isAuth0CallbackPath(window.location.pathname)) {
        return;
    }

    const value = withSessionStorage({
        fallback: null,
        read: storage => storage.getItem(POST_LOGIN_INTENT_SESSION_KEY),
    });

    if (!value) {
        return;
    }

    let intent: unknown;
    try {
        intent = JSON.parse(value);
    } catch {
        discardPostLoginIntent();
        return;
    }

    if (!isPostLoginIntent(intent)) {
        discardPostLoginIntent();
        return;
    }

    if (intent.action === action) {
        discardPostLoginIntent();
        await callback(intent.params as Extract<PostLoginIntent, { action: TAction }>['params']);
    }
}
